/**
 * Frente C — seção colapsável de governança por-etapa do editor de Playbook.
 *
 * Controlado: recebe a etapa e devolve a etapa inteira via `onChange` (os
 * setters de `step-governance-ui` são omit-on-default → dirty-tracking limpo).
 * Progressive disclosure: fechada por padrão; abre já aberta se a etapa já tem
 * governança. Revela os eixos por FORÇA (honesto sobre onde cada um atua):
 * `flexivel` só o seletor · `guiado` + obrigatoriedade/confirmação (prompt) ·
 * `duro` + sinal/bloqueios + banner de status do gate (arma × não-arma).
 */

import { ChevronDown, ChevronRight, Lock } from 'lucide-react';
import { useState } from 'react';

import {
  conductorActionLabel,
  CONFIRMATION_OPTIONS,
  ENFORCEMENT_OPTIONS,
  OBLIGATION_OPTIONS,
} from '../playbook-labels';
import {
  DEFAULT_ENFORCEMENT,
  effectiveBlockedList,
  gateGaps,
  hasGovernance,
  setBlockedActions,
  setBlockedTools,
  setConfirmation,
  setEnforcement,
  setObligation,
  setSuccessTool,
  stepArmsGate,
} from '../step-governance-ui';

import { ChipListEditor } from './ChipListEditor';

import type { PlaybookStep, StepConfirmation, StepObligation } from '../types';

export function StepGovernanceEditor({
  step,
  onChange,
  indexLabel,
}: {
  step: PlaybookStep;
  onChange: (next: PlaybookStep) => void;
  /** Índice legível (ex.: "1") para rótulos de acessibilidade. */
  indexLabel: string;
}): JSX.Element {
  const [open, setOpen] = useState<boolean>(() => hasGovernance(step));

  const enforcement = step.enforcement ?? DEFAULT_ENFORCEMENT;
  const showDetail = enforcement !== 'flexivel';
  const isHard = enforcement === 'duro';
  const arms = stepArmsGate(step);
  const gaps = gateGaps(step);
  const forceLabel = ENFORCEMENT_OPTIONS.find((o) => o.value === enforcement)?.label ?? enforcement;

  const blockedList = effectiveBlockedList(step).map(conductorActionLabel);

  return (
    <div className="pb-step-gov">
      <button
        type="button"
        className="pb-step-gov__toggle"
        aria-expanded={open}
        aria-label={`Governança da etapa ${indexLabel}: ${forceLabel}${
          isHard && arms ? ', trava ativa' : ''
        }`}
        onClick={() => {
          setOpen((v) => !v);
        }}
      >
        {open ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
        <span className="pb-step-gov__toggle-label">Governança</span>
        <span className="pb-step-gov__summary">
          {forceLabel}
          {isHard && arms && (
            <span className="pb-step-gov__lock">
              <Lock size={12} /> trava ativa
            </span>
          )}
        </span>
      </button>

      {open && (
        <div className="pb-step-gov__body">
          <div className="pb-field">
            <span className="pb-field__label">Força</span>
            <div
              className="pb-radio-row"
              role="radiogroup"
              aria-label={`Força da etapa ${indexLabel}`}
            >
              {ENFORCEMENT_OPTIONS.map((opt) => {
                const selected = opt.value === enforcement;
                return (
                  <button
                    key={opt.value}
                    type="button"
                    role="radio"
                    aria-checked={selected}
                    className={
                      'pb-radio pb-radio--editable' + (selected ? ' pb-radio--selected' : '')
                    }
                    onClick={() => {
                      onChange(setEnforcement(step, opt.value));
                    }}
                  >
                    <span className="pb-radio__dot" />
                    <span className="pb-radio__main">
                      <span className="pb-radio__label">{opt.label}</span>
                      <span className="pb-radio__desc">{opt.desc}</span>
                    </span>
                  </button>
                );
              })}
            </div>
          </div>

          {showDetail && (
            <>
              <div className="pb-field">
                <span className="pb-field__label">Obrigatoriedade</span>
                <select
                  className="pb-select"
                  value={step.obligation ?? 'optional'}
                  aria-label={`Obrigatoriedade da etapa ${indexLabel}`}
                  onChange={(e) => {
                    onChange(setObligation(step, e.target.value as StepObligation));
                  }}
                >
                  {OBLIGATION_OPTIONS.map((o) => (
                    <option key={o.value} value={o.value}>
                      {o.label}
                    </option>
                  ))}
                </select>
              </div>

              <div className="pb-field">
                <span className="pb-field__label">Confirmação</span>
                <select
                  className="pb-select"
                  value={step.confirmation ?? 'none'}
                  aria-label={`Confirmação da etapa ${indexLabel}`}
                  onChange={(e) => {
                    onChange(setConfirmation(step, e.target.value as StepConfirmation));
                  }}
                >
                  {CONFIRMATION_OPTIONS.map((o) => (
                    <option key={o.value} value={o.value}>
                      {o.label}
                    </option>
                  ))}
                </select>
              </div>
            </>
          )}

          {enforcement === 'guiado' && (
            <p className="pb-panel__hint">
              Protocolo guiado: a IA segue no raciocínio (via prompt), sem bloqueio de código.
            </p>
          )}

          {isHard && (
            <>
              <div className="pb-field">
                <span className="pb-field__label">Sinal de sucesso (ferramenta)</span>
                <input
                  type="text"
                  className="pb-input pb-input--mono"
                  value={step.successSignal?.tool ?? ''}
                  placeholder="ex.: connection-status"
                  aria-label={`Ferramenta que comprova a etapa ${indexLabel}`}
                  onChange={(e) => {
                    onChange(setSuccessTool(step, e.target.value));
                  }}
                />
                <span className="pb-panel__hint">
                  Nome exato da ferramenta que comprova a etapa (sinal determinístico). Sem ele, a
                  trava dura não arma.
                </span>
              </div>

              <div className="pb-field">
                <span className="pb-field__label">Bloqueia ações até cumprir</span>
                <ChipListEditor
                  values={step.blocks?.actions ?? []}
                  onChange={(next) => {
                    onChange(setBlockedActions(step, next));
                  }}
                  placeholder="ex.: escalate"
                  mono
                  ariaLabel={`Ações bloqueadas na etapa ${indexLabel}`}
                />
                <span className="pb-panel__hint">
                  Vazio = bloqueia escalar e encerrar (padrão).
                </span>
              </div>

              <div className="pb-field">
                <span className="pb-field__label">Bloqueia ferramentas até cumprir</span>
                <ChipListEditor
                  values={step.blocks?.tools ?? []}
                  onChange={(next) => {
                    onChange(setBlockedTools(step, next));
                  }}
                  placeholder="ex.: schedule-visit"
                  mono
                  ariaLabel={`Ferramentas bloqueadas na etapa ${indexLabel}`}
                />
              </div>

              <div
                className={
                  'pb-step-gov__status ' +
                  (arms ? 'pb-step-gov__status--gate' : 'pb-step-gov__status--warn')
                }
                role="status"
              >
                {arms ? (
                  <>
                    <Lock size={12} /> Trava ativa: bloqueia{' '}
                    <strong>{blockedList.join(', ')}</strong> até{' '}
                    <strong>{step.successSignal?.tool}</strong> rodar.
                  </>
                ) : (
                  <>A trava dura ainda não arma — falta {gaps.join(' e ')}.</>
                )}
              </div>
            </>
          )}
        </div>
      )}
    </div>
  );
}
